import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Zap } from 'lucide-react';
import { ParkingArea, ParkingSpot, ParkingSpotInput } from '../../types/parking';
import { ParkingSpotType } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { Building } from '../../types/protocols';
import { BaseComponentImpl, RoomImpl } from '../../types/implementations';
import RemovePlacementField from '../../Components/RentableDetail/RemovePlacementField';
import { RentablePhotoActions, RentablePhotosField, useStagedPhotos } from '../../Components/RentableDetail/RentablePhotosField';
import ComponentPickerModal from '../Förvaltning/components/ComponentPickerModal';
import {
    areaRoomMissing,
    chargerCandidateKeys,
    chargerOtherRoomKey,
    chargerSortRank,
    chargerSpotLinkLabel,
    chargerSpotLinks,
} from '../../Helpers/parkingUtils';

interface ParkingSpotModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (input: ParkingSpotInput) => Promise<void>;
    building: Building;
    area: ParkingArea;
    /** All parking areas of the building, used to show which spots a charger is linked to. */
    areas: Record<string, ParkingArea> | ParkingArea[];
    /** When given, the modal edits this spot; otherwise it creates one. */
    spot?: ParkingSpot;
    /** Pre-filled values for a new spot (series placement). */
    initialValues?: Partial<ParkingSpotInput>;
    submitting?: boolean;
    /** Editing a spot with a marker on the current drawing: offers "Ta bort placering", run after the spot is saved. */
    onRemoveLocation?: () => Promise<void>;
    /** Editing: the Foton field; changes apply after the object is saved, so Avbryt changes nothing. */
    photoActions?: RentablePhotoActions;
}

const ParkingSpotModal: React.FC<ParkingSpotModalProps> = ({
    show, onHide, onSubmit, building, area, areas, spot, initialValues, submitting = false, onRemoveLocation, photoActions
}) => {
    const stagedPhotos = useStagedPhotos('parking');
    const { reset: resetPhotos } = stagedPhotos;
    useEffect(() => {
        if (show) resetPhotos();
    }, [show, resetPhotos]);
    const busy = submitting || stagedPhotos.busy;
    const isEditing = !!spot;
    const [number, setNumber] = useState('');
    const [type, setType] = useState<ParkingSpotType>(ParkingSpotType.Car);
    const [ceilingHeightMm, setCeilingHeightMm] = useState('');
    const [chargerComponentKey, setChargerComponentKey] = useState<string | undefined>(undefined);
    const [hasOutlet, setHasOutlet] = useState(false);
    const [outletIdentifier, setOutletIdentifier] = useState('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [showChargerPicker, setShowChargerPicker] = useState(false);
    const [removeLocation, setRemoveLocation] = useState(false);

    useEffect(() => {
        if (!show) return;
        const source: Partial<ParkingSpotInput> = spot || initialValues || {};
        setNumber(source.number || '');
        setType(source.type || ParkingSpotType.Car);
        setCeilingHeightMm(source.ceilingHeight ? String(Math.round(source.ceilingHeight * 1000)) : '');
        setChargerComponentKey(source.chargerComponentKey || undefined);
        setHasOutlet(source.hasOutlet === true);
        setOutletIdentifier(source.outletIdentifier || '');
        setComment(source.comment || '');
        setRemoveLocation(false);
        setError(null);
    }, [show, spot, initialValues]);

    const chargerName = useMemo(() => {
        if (!chargerComponentKey) return null;
        const component = building.model.structure?.components?.[chargerComponentKey];
        return component ? new BaseComponentImpl(component).displayName(building) : 'Laddare saknas i byggnaden';
    }, [chargerComponentKey, building]);

    // The picker always lists the spot's saved and currently chosen charger,
    // so they stay selectable even when they no longer match the area's room.
    const savedChargerKey = spot?.chargerComponentKey || undefined;
    const candidateKeys = useMemo(() => {
        const structure = building.model.structure;
        return new Set([
            ...chargerCandidateKeys(structure, area, savedChargerKey),
            ...chargerCandidateKeys(structure, area, chargerComponentKey),
        ]);
    }, [building, area, savedChargerKey, chargerComponentKey]);
    const chargerFilter = useCallback(
        (component: BaseComponentImpl) => candidateKeys.has(component.key),
        [candidateKeys]
    );
    const roomMissing = areaRoomMissing(area, building.model.structure);

    // Per candidate: spot links, and the other room it belongs to when it is not in the garage's room.
    const chargerInfo = useMemo(() => {
        const structure = building.model.structure;
        const exclude = spot ? { areaKey: area.key, spotKey: spot.key } : undefined;
        const info = new Map<string, { detail: string; rank: number }>();
        candidateKeys.forEach(key => {
            const component = structure?.components?.[key];
            if (!component) return;
            const links = chargerSpotLinks(areas, key, exclude);
            const otherRoomKey = chargerOtherRoomKey(area, structure, component);
            const linkLabel = chargerSpotLinkLabel(links, area.key);
            let detail = linkLabel;
            if (otherRoomKey !== undefined) {
                const otherRoom = otherRoomKey ? structure?.rooms?.[otherRoomKey] : undefined;
                detail = otherRoom
                    ? `Annat rum: ${new RoomImpl(otherRoom).displayName()} · ${linkLabel}`
                    : `Utan rum · ${linkLabel}`;
            }
            info.set(key, { detail, rank: chargerSortRank(otherRoomKey !== undefined, links.length > 0) });
        });
        return info;
    }, [candidateKeys, building, areas, area, spot]);
    const getChargerDetail = useCallback(
        (component: BaseComponentImpl) => chargerInfo.get(component.key)?.detail,
        [chargerInfo]
    );
    const getChargerSortRank = useCallback(
        (component: BaseComponentImpl) => chargerInfo.get(component.key)?.rank ?? 0,
        [chargerInfo]
    );

    const areaDefaultMm =area.defaultCeilingHeight ? Math.round(area.defaultCeilingHeight * 1000) : undefined;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!number.trim()) {
            setError('Platsnummer krävs');
            return;
        }
        let ceilingHeight: number | undefined;
        if (ceilingHeightMm.trim()) {
            const mm = parseInt(ceilingHeightMm, 10);
            if (!Number.isFinite(mm) || mm <= 0) {
                setError('Fri höjd måste vara ett positivt antal millimeter');
                return;
            }
            ceilingHeight = mm / 1000;
        }

        try {
            await onSubmit({
                number: number.trim(),
                type,
                ceilingHeight,
                chargerComponentKey,
                hasOutlet: hasOutlet || undefined,
                outletIdentifier: hasOutlet ? (outletIdentifier.trim() || undefined) : undefined,
                comment: comment.trim() || undefined,
            });
            if (removeLocation && onRemoveLocation) await onRemoveLocation();
            if (isEditing && photoActions) await stagedPhotos.apply(photoActions);
            onHide();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara parkeringsplatsen');
        }
    };

    return (
        <>
            <Modal show={show && !showChargerPicker} onHide={busy ? () => undefined : onHide} centered>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>{isEditing ? 'Redigera parkeringsplats' : 'Ny parkeringsplats'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                        <Form.Group className="mb-3">
                            <Form.Label>Platsnummer *</Form.Label>
                            <Form.Control
                                type="text"
                                value={number}
                                onChange={e => setNumber(e.target.value.toUpperCase())}
                                placeholder="T.ex. 12 eller P12"
                                autoFocus
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Typ av plats</Form.Label>
                            <Form.Select value={type} onChange={e => setType(e.target.value as ParkingSpotType)}>
                                {Object.values(ParkingSpotType).map(value => (
                                    <option key={value} value={value}>{translate(value)}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Fri höjd (mm)</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                value={ceilingHeightMm}
                                onChange={e => setCeilingHeightMm(e.target.value)}
                                placeholder={areaDefaultMm ? `${areaDefaultMm} (områdets)` : 'Valfritt'}
                            />
                            <Form.Text muted>Lämna tomt för att använda områdets förinställda höjd.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Laddare</Form.Label>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span>
                                    {chargerName
                                        ? <><Zap size={14} className="me-1" />{chargerName}</>
                                        : <span className="text-muted">Ingen laddare</span>}
                                </span>
                                {candidateKeys.size > 0 ? (
                                    <Button variant="outline-secondary" size="sm" onClick={() => setShowChargerPicker(true)}>
                                        {chargerComponentKey ? 'Byt' : 'Välj laddare'}
                                    </Button>
                                ) : (
                                    <span className="text-muted small">Inga laddare att välja</span>
                                )}
                                {chargerComponentKey && (
                                    <Button variant="outline-danger" size="sm" onClick={() => setChargerComponentKey(undefined)}>
                                        Ta bort koppling
                                    </Button>
                                )}
                            </div>
                            <Form.Text muted>
                                Laddare registreras som fastighetsteknik (el) i appen; här kopplas en befintlig laddare till platsen.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="parking-spot-outlet"
                                label="Motorvärmaruttag"
                                checked={hasOutlet}
                                onChange={e => setHasOutlet(e.target.checked)}
                            />
                        </Form.Group>

                        {hasOutlet && (
                            <Form.Group className="mb-3">
                                <Form.Label>Uttagets nummer</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={outletIdentifier}
                                    onChange={e => setOutletIdentifier(e.target.value.toUpperCase())}
                                    placeholder="Valfritt"
                                />
                            </Form.Group>
                        )}

                        {isEditing && onRemoveLocation && (
                            <RemovePlacementField marked={removeLocation} onChange={setRemoveLocation} hint="Platsen finns kvar i listan och kan placeras igen." />
                        )}

                        {isEditing && photoActions && <RentablePhotosField actions={photoActions} staged={stagedPhotos} />}

                        <Form.Group className="mb-1">
                            <Form.Label>Kommentar</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide} disabled={busy}>Avbryt</Button>
                        <Button type="submit" variant="primary" disabled={busy}>
                            {busy ? <><Spinner size="sm" animation="border" /> Sparar...</> : 'Spara'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ComponentPickerModal
                show={show && showChargerPicker}
                onHide={() => setShowChargerPicker(false)}
                building={building}
                currentComponentKey={chargerComponentKey}
                filter={chargerFilter}
                hint={roomMissing ? 'Områdets rum finns inte längre' : undefined}
                getItemDetail={getChargerDetail}
                getItemSortRank={getChargerSortRank}
                onSelect={(key) => {
                    setChargerComponentKey(key || undefined);
                    setShowChargerPicker(false);
                }}
            />
        </>
    );
};

export default ParkingSpotModal;
